import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Typography, Button, Descriptions, Alert, Spin, Progress, Modal, message, Table, Grid } from 'antd'
import { RobotOutlined, PlayCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { dashboardAPI } from '../services/api'

const { Title, Text, Paragraph } = Typography
const { useBreakpoint } = Grid

const ModelManagement: React.FC = () => {
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const [loading, setLoading] = useState(true)
  const [training, setTraining] = useState(false)
  const [trainingStatus, setTrainingStatus] = useState<string>('idle')
  const [trainingResult, setTrainingResult] = useState<any>(null)
  const [modelInfo, setModelInfo] = useState<any>(null)
  const [restrictiveListsInfo, setRestrictiveListsInfo] = useState<any>(null)

  const loadModelData = async () => {
    try {
      setLoading(true)
      const [modelData, restrictiveData] = await Promise.all([
        dashboardAPI.getModelInfo(),
        dashboardAPI.getRestrictiveListsInfo()
      ])
      
      setModelInfo(modelData.model)
      setRestrictiveListsInfo(restrictiveData.lists)
    } catch (error) {
      message.error('Error al cargar información del modelo')
      console.error('Model info load error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadModelData()
  }, [])

  // Poll training status every 5 s while running
  useEffect(() => {
    if (trainingStatus !== 'running') return
    const interval = setInterval(async () => {
      try {
        const s = await dashboardAPI.getTrainingStatus()
        setTrainingStatus(s.status)
        if (s.status === 'completed') {
          setTrainingResult(s.result ?? null)
          setTraining(false)
          message.success(`Entrenamiento completado. F1: ${(s.result?.training_metrics?.f1_score * 100 ?? 0).toFixed(1)}%`)
          await loadModelData()
          clearInterval(interval)
        } else if (s.status === 'failed') {
          setTraining(false)
          message.error(`Error en el entrenamiento: ${s.error || 'desconocido'}`)
          clearInterval(interval)
        }
      } catch { /* red momentáneamente caída — reintentar en el siguiente tick */ }
    }, 5000)
    return () => clearInterval(interval)
  }, [trainingStatus])

  const handleTrainModel = async () => {
    Modal.confirm({
      title: 'Entrenar Modelo de IA',
      content: (
        <div>
          <Paragraph>¿Desea iniciar el entrenamiento del modelo de IA?</Paragraph>
          <Paragraph>
            <Text type="secondary">
              El proceso corre en background y puede tomar varios minutos.
              Puedes seguir usando la plataforma mientras entrena.
            </Text>
          </Paragraph>
        </div>
      ),
      icon: <RobotOutlined />,
      onOk: async () => {
        try {
          setTraining(true)
          setTrainingResult(null)
          await dashboardAPI.trainModel()
          setTrainingStatus('running')
          message.info('Entrenamiento iniciado en background — actualizando cada 5 s…')
        } catch (error: any) {
          setTraining(false)
          message.error(`Error al iniciar: ${error.response?.data?.detail || error.message}`)
        }
      },
    })
  }

  const featureImportanceColumns = [
    {
      title: 'Variable',
      dataIndex: 'feature',
      key: 'feature',
    },
    {
      title: 'Importancia',
      dataIndex: 'importance',
      key: 'importance',
      render: (value: number) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Progress 
            percent={value * 100} 
            size="small" 
            style={{ width: 100, marginRight: 8 }} 
            showInfo={false}
          />
          <Text>{(value * 100).toFixed(1)}%</Text>
        </div>
      ),
    },
  ]

  const featureImportanceData = modelInfo?.feature_importance 
    ? Object.entries(modelInfo.feature_importance)
        .sort(([,a], [,b]) => (b as number) - (a as number))
        .slice(0, 10)
        .map(([feature, importance]) => ({
          feature: feature.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          importance: importance as number
        }))
    : []

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Title level={2} style={{ marginTop: 0 }}>Gestión del Modelo de IA</Title>

      {/* Estado del Modelo */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} xl={16}>
          <Card title="Estado del Modelo" extra={<RobotOutlined />}>
            {modelInfo ? (
              <Descriptions column={2}>
                <Descriptions.Item label="Estado">
                  {modelInfo.is_trained ? (
                    <span style={{ color: '#52c41a' }}>✓ Entrenado</span>
                  ) : (
                    <span style={{ color: '#faad14' }}>⚠ Sin entrenar</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Tipo de Modelo">
                  {modelInfo.model_type || 'RandomForestClassifier'}
                </Descriptions.Item>
                <Descriptions.Item label="Precisión">
                  {modelInfo.metrics?.accuracy 
                    ? `${(modelInfo.metrics.accuracy * 100).toFixed(1)}%`
                    : 'N/A'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="F1 Score">
                  {modelInfo.metrics?.f1_score 
                    ? `${(modelInfo.metrics.f1_score * 100).toFixed(1)}%`
                    : 'N/A'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Datos de Entrenamiento">
                  {modelInfo.metrics?.training_samples || 'N/A'} casos
                </Descriptions.Item>
                <Descriptions.Item label="Última Actualización">
                  {modelInfo.metrics?.training_date 
                    ? new Date(modelInfo.metrics.training_date).toLocaleDateString('es-ES')
                    : 'Nunca'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Umbral Rechazo" span={2}>
                  {(modelInfo.thresholds?.rejection * 100 || 30).toFixed(0)}% - 
                  Umbral Aprobación: {(modelInfo.thresholds?.approval * 100 || 70).toFixed(0)}%
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                message="Información no disponible"
                description="No se pudo cargar la información del modelo de IA"
                type="warning"
                showIcon
              />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8}>
          <Card title="Acciones">
            <div style={{ textAlign: 'center' }}>
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                loading={training}
                onClick={handleTrainModel}
                style={{ marginBottom: 16 }}
                block
              >
                {training ? 'Entrenando...' : 'Entrenar Modelo'}
              </Button>
              
              {!modelInfo?.is_trained && (
                <Alert
                  message="Modelo sin entrenar"
                  description="El sistema está usando lógica basada en reglas. Entrene el modelo para habilitar IA."
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  size="small"
                />
              )}

              {modelInfo?.is_trained && modelInfo.metrics?.accuracy && modelInfo.metrics.accuracy < 0.8 && (
                <Alert
                  message="Precisión baja"
                  description={`Precisión actual: ${(modelInfo.metrics.accuracy * 100).toFixed(1)}%. Considere reentrenar con más datos.`}
                  type="warning"
                  showIcon
                  size="small"
                />
              )}

              {modelInfo?.is_trained && modelInfo.metrics?.accuracy && modelInfo.metrics.accuracy >= 0.8 && (
                <Alert
                  message="Modelo óptimo"
                  description={`Precisión: ${(modelInfo.metrics.accuracy * 100).toFixed(1)}%. El modelo está funcionando correctamente.`}
                  type="success"
                  showIcon
                  size="small"
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Métricas Detalladas */}
      {modelInfo?.metrics?.class_metrics && (
        <Card title="Métricas por Clase" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card size="small" title="Rechazos">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Precisión">
                    {(modelInfo.metrics.class_metrics.REJECTED?.precision * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Recall">
                    {(modelInfo.metrics.class_metrics.REJECTED?.recall * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="F1-Score">
                    {(modelInfo.metrics.class_metrics.REJECTED?.['f1-score'] * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Revisión Manual">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Precisión">
                    {(modelInfo.metrics.class_metrics.MANUAL_REVIEW?.precision * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Recall">
                    {(modelInfo.metrics.class_metrics.MANUAL_REVIEW?.recall * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="F1-Score">
                    {(modelInfo.metrics.class_metrics.MANUAL_REVIEW?.['f1-score'] * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card size="small" title="Aprobaciones">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Precisión">
                    {(modelInfo.metrics.class_metrics.APPROVED?.precision * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Recall">
                    {(modelInfo.metrics.class_metrics.APPROVED?.recall * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                  <Descriptions.Item label="F1-Score">
                    {(modelInfo.metrics.class_metrics.APPROVED?.['f1-score'] * 100 || 0).toFixed(1)}%
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* Variables Más Importantes */}
      {featureImportanceData.length > 0 && (
        <Card title="Variables Más Influyentes" style={{ marginBottom: 24 }}>
          <Table
            columns={featureImportanceColumns}
            dataSource={featureImportanceData}
            rowKey="feature"
            pagination={false}
            scroll={{ x: isMobile ? 520 : undefined }}
            size="small"
          />
        </Card>
      )}

      {/* Listas Restrictivas */}
      <Card title="Estado de Listas Restrictivas" extra={<InfoCircleOutlined />}>
        {restrictiveListsInfo ? (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card size="small" title="Lista Negra Interna">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Registros">
                    {restrictiveListsInfo.blacklist?.count || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Última Actualización">
                    {restrictiveListsInfo.blacklist?.last_updated 
                      ? new Date(restrictiveListsInfo.blacklist.last_updated).toLocaleDateString('es-ES')
                      : 'N/A'
                    }
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title="Lista Gris Interna">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label="Registros">
                    {restrictiveListsInfo.greylist?.count || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="Última Actualización">
                    {restrictiveListsInfo.greylist?.last_updated 
                      ? new Date(restrictiveListsInfo.greylist.last_updated).toLocaleDateString('es-ES')
                      : 'N/A'
                    }
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        ) : (
          <Alert
            message="Información no disponible"
            description="No se pudo cargar la información de listas restrictivas"
            type="warning"
            showIcon
          />
        )}
      </Card>
    </div>
  )
}

export default ModelManagement